import React, { useCallback, useEffect, useState } from "react";
import type { User } from "firebase/auth";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  deletePlanRemindersSchedulerJob,
  fetchAtRiskOrganizations,
  fetchPlanReminders,
  runPlanReminders,
  updatePlanReminders,
  upsertPlanRemindersSchedulerJob,
  type AtRiskOrganization,
  type PlanReminderPaymentMethod,
  type PlanRemindersConfig,
  type PlanRemindersRunResult,
} from "../../api/planReminders";

const TIMEZONE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "America/Caracas", label: "America/Caracas (Venezuela)" },
  { value: "UTC", label: "UTC" },
  { value: "America/New_York", label: "America/New_York (US Eastern)" },
  { value: "America/Bogota", label: "America/Bogota" },
  { value: "America/Lima", label: "America/Lima" },
  { value: "America/Mexico_City", label: "America/Mexico_City" },
  { value: "America/Santiago", label: "America/Santiago" },
  {
    value: "America/Argentina/Buenos_Aires",
    label: "America/Argentina/Buenos_Aires",
  },
  { value: "Europe/Madrid", label: "Europe/Madrid" },
];

const MINUTE_OPTIONS = [0, 15, 30, 45];

const METHOD_LABELS: Record<PlanReminderPaymentMethod, string> = {
  ves: "Bolívares (VES)",
  stripe: "Card (Stripe)",
  binance: "Binance Pay",
};

const PageIntro = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <header className="content__header mb-4">
    <div>
      <h1 className="page-header__title">{title}</h1>
      <p className="page-header__lead">{description}</p>
    </div>
  </header>
);

const formatRunAt = (value: string | null | undefined): string => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const formatPeriodEnd = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const PlanRemindersPage: React.FC<{ user: User }> = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [running, setRunning] = useState(false);
  const [atRiskLoading, setAtRiskLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [config, setConfig] = useState<PlanRemindersConfig | null>(null);
  const [lastResult, setLastResult] = useState<PlanRemindersRunResult | null>(
    null
  );
  const [atRisk, setAtRisk] = useState<AtRiskOrganization[]>([]);
  const [atRiskMeta, setAtRiskMeta] = useState<{
    atRiskDaysAfterGrace: number;
    vesGraceDays: number;
  } | null>(null);

  const [daysBefore, setDaysBefore] = useState(2);
  const [remindOnExpiration, setRemindOnExpiration] = useState(true);
  const [remindMidGrace, setRemindMidGrace] = useState(true);
  const [midGraceDay, setMidGraceDay] = useState(5);
  const [remindGraceEnd, setRemindGraceEnd] = useState(true);
  const [atRiskDaysAfterGrace, setAtRiskDaysAfterGrace] = useState(15);
  const [includeTrialing, setIncludeTrialing] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<
    PlanReminderPaymentMethod[]
  >(["ves"]);
  const [timeZone, setTimeZone] = useState("America/Caracas");
  const [timeZonePreset, setTimeZonePreset] = useState("America/Caracas");
  const [scheduleHour, setScheduleHour] = useState(9);
  const [scheduleMinute, setScheduleMinute] = useState(0);

  const applyConfig = useCallback((next: PlanRemindersConfig) => {
    setConfig(next);
    setDaysBefore(next.daysBeforeRenewal);
    setRemindOnExpiration(next.remindOnExpiration);
    setRemindMidGrace(next.remindMidGrace);
    setMidGraceDay(next.midGraceDay);
    setRemindGraceEnd(next.remindGraceEnd);
    setAtRiskDaysAfterGrace(next.atRiskDaysAfterGrace);
    setIncludeTrialing(next.includeTrialing);
    setPaymentMethods(next.paymentMethods);
    setTimeZone(next.timeZone);
    const preset = TIMEZONE_OPTIONS.some((o) => o.value === next.timeZone)
      ? next.timeZone
      : "__custom__";
    setTimeZonePreset(preset);
    setScheduleHour(next.scheduleHour);
    setScheduleMinute(next.scheduleMinute);
  }, []);

  const loadAtRisk = useCallback(async () => {
    setAtRiskLoading(true);
    try {
      const data = await fetchAtRiskOrganizations(user);
      setAtRisk(data.organizations);
      setAtRiskMeta({
        atRiskDaysAfterGrace: data.atRiskDaysAfterGrace,
        vesGraceDays: data.vesGraceDays,
      });
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : "Could not load at-risk organizations."
      );
    } finally {
      setAtRiskLoading(false);
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPlanReminders(user)
      .then((data) => {
        if (!cancelled) applyConfig(data);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : "Could not load plan reminders."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, applyConfig]);

  useEffect(() => {
    if (!loading && config) {
      void loadAtRisk();
    }
  }, [loading, config, loadAtRisk]);

  const policyPatch = () => ({
    daysBeforeRenewal: daysBefore,
    remindOnExpiration,
    remindMidGrace,
    midGraceDay,
    remindGraceEnd,
    atRiskDaysAfterGrace,
    includeTrialing,
    paymentMethods,
    timeZone,
    scheduleHour,
    scheduleMinute,
  });

  const toggleMethod = (method: PlanReminderPaymentMethod, on: boolean) => {
    setPaymentMethods((prev) => {
      if (on) {
        return prev.includes(method) ? prev : [...prev, method];
      }
      return prev.filter((m) => m !== method);
    });
    setSuccess(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (paymentMethods.length === 0) {
      setError("Select at least one payment method (VES is recommended).");
      return;
    }
    if (daysBefore < 1 || daysBefore > 30) {
      setError("Days before renewal must be between 1 and 30.");
      return;
    }
    setSaving(true);
    try {
      const saved = await updatePlanReminders(user, policyPatch());
      applyConfig(saved);
      await loadAtRisk();
      setSuccess(
        "Reminder settings saved. If the daily job is already enabled, click Update schedule to push the new time to Cloud Scheduler."
      );
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Could not save plan reminders."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEnableSchedule = async () => {
    setError(null);
    setSuccess(null);
    setScheduling(true);
    try {
      const saved = await updatePlanReminders(user, policyPatch());
      applyConfig(saved);
      const scheduled = await upsertPlanRemindersSchedulerJob(user);
      applyConfig(scheduled);
      setSuccess(
        `Daily Cloud Scheduler job enabled (${scheduled.schedulerSchedule} ${scheduled.timeZone}).`
      );
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not enable the scheduler job."
      );
    } finally {
      setScheduling(false);
    }
  };

  const handleDisableSchedule = async () => {
    setError(null);
    setSuccess(null);
    setScheduling(true);
    try {
      const saved = await deletePlanRemindersSchedulerJob(user);
      applyConfig(saved);
      setSuccess("Daily Cloud Scheduler job disabled.");
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not disable the scheduler job."
      );
    } finally {
      setScheduling(false);
    }
  };

  const handleRun = async (dryRun: boolean) => {
    setError(null);
    setSuccess(null);
    setRunning(true);
    try {
      const result = await runPlanReminders(user, { dryRun });
      setLastResult(result);
      if (!dryRun) {
        const refreshed = await fetchPlanReminders(user);
        applyConfig(refreshed);
      }
      setSuccess(
        dryRun
          ? `Dry run: ${result.matched} org(s) would be reminded (${result.scanned} scanned).`
          : `Sent ${result.sent} reminder(s). Matched ${result.matched} of ${result.scanned} scanned.`
      );
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Could not run plan reminders."
      );
    } finally {
      setRunning(false);
    }
  };

  const vesGraceDays = config?.vesGraceDays ?? 10;

  return (
    <>
      <PageIntro
        title="Plan reminders"
        description="Daily emails across renewal, expiration, and VES grace — plus a list of recently lapsed orgs for manual outreach."
      />
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading || !config ? (
        <div className="d-flex align-items-center gap-2 text-muted">
          <Spinner animation="border" size="sm" />
          <span>Loading…</span>
        </div>
      ) : (
        <>
          <Row className="g-4">
            <Col lg={7}>
              <Card className="shadow-sm">
                <Card.Header className="border-0 d-flex justify-content-between align-items-center">
                  <Card.Title className="mb-0">Reminder policy</Card.Title>
                  {config.schedulerEnabled ? (
                    <Badge bg="success">Scheduler on</Badge>
                  ) : (
                    <Badge bg="secondary">Scheduler off</Badge>
                  )}
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleSave}>
                    <Form.Group className="mb-3">
                      <Form.Label>Days before period end</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        max={30}
                        value={daysBefore}
                        onChange={(e) => setDaysBefore(Number(e.target.value))}
                        disabled={saving || scheduling}
                      />
                      <Form.Text muted>
                        Renewal nudge when the period ends in exactly this many
                        calendar days (default 2).
                      </Form.Text>
                    </Form.Group>

                    <Form.Check
                      type="switch"
                      id="remind-on-expiration"
                      className="mb-2"
                      label="Remind on the calendar day the plan expires"
                      checked={remindOnExpiration}
                      onChange={(e) => setRemindOnExpiration(e.target.checked)}
                      disabled={saving || scheduling}
                    />

                    <Form.Check
                      type="switch"
                      id="remind-mid-grace"
                      className="mb-2"
                      label={`Remind mid-grace (day ${midGraceDay} of ${vesGraceDays})`}
                      checked={remindMidGrace}
                      onChange={(e) => setRemindMidGrace(e.target.checked)}
                      disabled={saving || scheduling}
                    />
                    {remindMidGrace && (
                      <Form.Group className="mb-3 ms-4">
                        <Form.Label>Mid-grace day</Form.Label>
                        <Form.Control
                          type="number"
                          min={1}
                          max={Math.max(1, vesGraceDays - 1)}
                          value={midGraceDay}
                          onChange={(e) =>
                            setMidGraceDay(Number(e.target.value))
                          }
                          disabled={saving || scheduling}
                        />
                      </Form.Group>
                    )}

                    <Form.Check
                      type="switch"
                      id="remind-grace-end"
                      className="mb-3"
                      label={`Remind on last day of grace (day ${vesGraceDays})`}
                      checked={remindGraceEnd}
                      onChange={(e) => setRemindGraceEnd(e.target.checked)}
                      disabled={saving || scheduling}
                    />

                    <Form.Group className="mb-3">
                      <Form.Label>
                        At-risk list window (days after grace ends)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        max={60}
                        value={atRiskDaysAfterGrace}
                        onChange={(e) =>
                          setAtRiskDaysAfterGrace(Number(e.target.value))
                        }
                        disabled={saving || scheduling}
                      />
                      <Form.Text muted>
                        Orgs whose grace ended 1–{atRiskDaysAfterGrace} days ago
                        appear below for manual outreach (no auto email).
                      </Form.Text>
                    </Form.Group>

                    <Form.Check
                      type="switch"
                      id="include-trialing"
                      className="mb-3"
                      label="Include trialing organizations"
                      checked={includeTrialing}
                      onChange={(e) => setIncludeTrialing(e.target.checked)}
                      disabled={saving || scheduling}
                    />

                    <fieldset className="mb-3">
                      <legend className="fs-6 mb-2">
                        Audience payment methods
                      </legend>
                      {(
                        [
                          "ves",
                          "stripe",
                          "binance",
                        ] as PlanReminderPaymentMethod[]
                      ).map((method) => (
                        <Form.Check
                          key={method}
                          type="checkbox"
                          id={`pm-${method}`}
                          className="mb-1"
                          label={METHOD_LABELS[method]}
                          checked={paymentMethods.includes(method)}
                          onChange={(e) =>
                            toggleMethod(method, e.target.checked)
                          }
                          disabled={saving || scheduling}
                        />
                      ))}
                      <Form.Text muted>
                        VES is the main audience: Stripe renews automatically.
                      </Form.Text>
                    </fieldset>

                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Timezone</Form.Label>
                          <Form.Select
                            value={timeZonePreset}
                            onChange={(e) => {
                              const value = e.target.value;
                              setTimeZonePreset(value);
                              if (value !== "__custom__") {
                                setTimeZone(value);
                              }
                            }}
                            disabled={saving || scheduling}
                          >
                            {TIMEZONE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                            <option value="__custom__">Custom IANA…</option>
                          </Form.Select>
                          {timeZonePreset === "__custom__" && (
                            <Form.Control
                              className="mt-2"
                              value={timeZone}
                              onChange={(e) => setTimeZone(e.target.value)}
                              placeholder="America/Caracas"
                              disabled={saving || scheduling}
                            />
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Hour</Form.Label>
                          <Form.Select
                            value={scheduleHour}
                            onChange={(e) =>
                              setScheduleHour(Number(e.target.value))
                            }
                            disabled={saving || scheduling}
                          >
                            {Array.from({ length: 24 }, (_, h) => (
                              <option key={h} value={h}>
                                {String(h).padStart(2, "0")}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Minute</Form.Label>
                          <Form.Select
                            value={scheduleMinute}
                            onChange={(e) =>
                              setScheduleMinute(Number(e.target.value))
                            }
                            disabled={saving || scheduling}
                          >
                            {MINUTE_OPTIONS.map((m) => (
                              <option key={m} value={m}>
                                {String(m).padStart(2, "0")}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex flex-wrap gap-2">
                      <Button type="submit" variant="primary" disabled={saving}>
                        {saving ? "Saving…" : "Save settings"}
                      </Button>
                      {config.schedulerEnabled ? (
                        <>
                          <Button
                            type="button"
                            variant="outline-primary"
                            disabled={scheduling || saving}
                            onClick={handleEnableSchedule}
                          >
                            {scheduling ? "Updating…" : "Update schedule"}
                          </Button>
                          <Button
                            type="button"
                            variant="outline-danger"
                            disabled={scheduling}
                            onClick={handleDisableSchedule}
                          >
                            Disable schedule
                          </Button>
                        </>
                      ) : (
                        <Button
                          type="button"
                          variant="success"
                          disabled={scheduling || saving}
                          onClick={handleEnableSchedule}
                        >
                          {scheduling ? "Enabling…" : "Enable daily schedule"}
                        </Button>
                      )}
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="shadow-sm mb-4">
                <Card.Header className="border-0">
                  <Card.Title className="mb-0">Job status</Card.Title>
                </Card.Header>
                <Card.Body>
                  <dl className="mb-0">
                    <dt className="text-muted small">Cron</dt>
                    <dd>
                      <code>
                        {config.schedulerSchedule ||
                          `${scheduleMinute} ${scheduleHour} * * *`}
                      </code>{" "}
                      <span className="text-muted">({config.timeZone})</span>
                    </dd>
                    <dt className="text-muted small">Job name</dt>
                    <dd className="text-break">
                      {config.schedulerJobName || "—"}
                    </dd>
                    <dt className="text-muted small">Last run</dt>
                    <dd>{formatRunAt(config.lastRunAt)}</dd>
                    {config.lastRunSummary && (
                      <>
                        <dt className="text-muted small">Last summary</dt>
                        <dd>
                          sent {config.lastRunSummary.sent ?? 0}, matched{" "}
                          {config.lastRunSummary.matched ?? 0}, errors{" "}
                          {config.lastRunSummary.errors ?? 0}
                        </dd>
                      </>
                    )}
                  </dl>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <Button
                      variant="outline-secondary"
                      disabled={running}
                      onClick={() => handleRun(true)}
                    >
                      {running ? "Running…" : "Dry run"}
                    </Button>
                    <Button
                      variant="outline-primary"
                      disabled={running}
                      onClick={() => handleRun(false)}
                    >
                      {running ? "Running…" : "Run now"}
                    </Button>
                  </div>
                  <p className="text-muted small mt-3 mb-0">
                    Auto emails: {daysBefore}d before → expire day → mid-grace →
                    last grace day. At-risk list is manual only.
                  </p>
                </Card.Body>
              </Card>

              {lastResult && (
                <Card className="shadow-sm">
                  <Card.Header className="border-0">
                    <Card.Title className="mb-0">
                      {lastResult.dryRun ? "Dry run result" : "Run result"}
                    </Card.Title>
                  </Card.Header>
                  <Card.Body>
                    <ul className="mb-0 small">
                      <li>Scanned: {lastResult.scanned}</li>
                      <li>Matched: {lastResult.matched}</li>
                      <li>Sent: {lastResult.sent}</li>
                      <li>Already sent: {lastResult.skippedAlreadySent}</li>
                      <li>No email: {lastResult.skippedNoEmail}</li>
                      <li>Errors: {lastResult.errors}</li>
                    </ul>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>

          <Card className="shadow-sm mt-4">
            <Card.Header className="border-0 d-flex justify-content-between align-items-center">
              <div>
                <Card.Title className="mb-0">
                  At risk — grace ended recently
                </Card.Title>
                <div className="text-muted small">
                  Grace is {atRiskMeta?.vesGraceDays ?? vesGraceDays} days.
                  Showing orgs whose grace ended in the last{" "}
                  {atRiskMeta?.atRiskDaysAfterGrace ?? atRiskDaysAfterGrace}{" "}
                  days — reach out manually to win them back.
                </div>
              </div>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={atRiskLoading}
                onClick={() => void loadAtRisk()}
              >
                {atRiskLoading ? "Refreshing…" : "Refresh"}
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              {atRiskLoading && atRisk.length === 0 ? (
                <div className="p-3 d-flex align-items-center gap-2 text-muted">
                  <Spinner animation="border" size="sm" />
                  <span>Loading at-risk orgs…</span>
                </div>
              ) : atRisk.length === 0 ? (
                <p className="text-muted p-3 mb-0">
                  No organizations in this window right now.
                </p>
              ) : (
                <div className="table-responsive">
                  <Table hover className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Organization</th>
                        <th>Email</th>
                        <th>Plan</th>
                        <th>Period end</th>
                        <th>Grace ended</th>
                      </tr>
                    </thead>
                    <tbody>
                      {atRisk.map((org) => (
                        <tr key={org.id}>
                          <td>
                            <div className="fw-semibold">{org.name}</div>
                            <div className="text-muted small">{org.id}</div>
                          </td>
                          <td>
                            {org.email ? (
                              <a href={`mailto:${org.email}`}>{org.email}</a>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>{org.planName}</td>
                          <td>{formatPeriodEnd(org.currentPeriodEnd)}</td>
                          <td>
                            {org.graceEndedDaysAgo} day
                            {org.graceEndedDaysAgo === 1 ? "" : "s"} ago
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </>
  );
};

export default PlanRemindersPage;
